import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import * as XLSX from 'xlsx';
import Layout from '../components/layout/Layout';
import Header from '../components/layout/Header';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Upload, 
  FileSpreadsheet, 
  Download,
  CheckCircle,
  AlertTriangle,
  RefreshCw,
  Trash2,
  Building2,
  Shield,
  Megaphone,
  DollarSign,
  Users,
  FileDown,
  Calendar,
  ChevronRight,
  Info
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import { cn } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UploadCenter = () => {
  const { user } = useAuth();
  const { selectedYear } = useApp();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Get initial values from URL params (for deep-linking from email reminders)
  const urlFunction = searchParams.get('function');
  const urlCadence = searchParams.get('cadence');
  const urlYear = searchParams.get('year');
  const urlPeriod = searchParams.get('period');
  const urlTab = searchParams.get('tab');
  
  const [uploads, setUploads] = useState([]);
  const [selectedFunction, setSelectedFunction] = useState(urlFunction || 'brokerage');
  const [templateYear, setTemplateYear] = useState(urlYear ? Number(urlYear) : (selectedYear || 2025));
  const [selectedCadence, setSelectedCadence] = useState(urlCadence || 'monthly');
  const [selectedPeriod, setSelectedPeriod] = useState(urlPeriod ? Number(urlPeriod) : 1);
  const [availablePeriods, setAvailablePeriods] = useState([]);
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [loading, setLoading] = useState(true);
  const [validationResult, setValidationResult] = useState(null);
  const [showValidationDetails, setShowValidationDetails] = useState(false);
  const [templateInfo, setTemplateInfo] = useState(null);
  const [activeTab, setActiveTab] = useState(urlTab || 'templates');

  // Show notification if arriving from email link
  const [showDeepLinkBanner, setShowDeepLinkBanner] = useState(!!urlFunction && !!urlCadence);

  const departments = [
    { value: 'brokerage', label: 'Brokerage', icon: Building2, color: 'bg-blue-100 text-blue-700' },
    { value: 'mga', label: 'MGA', icon: Shield, color: 'bg-emerald-100 text-emerald-700' },
    { value: 'marketing', label: 'Marketing', icon: Megaphone, color: 'bg-violet-100 text-violet-700' },
    { value: 'finance', label: 'Finance', icon: DollarSign, color: 'bg-amber-100 text-amber-700' },
    { value: 'people', label: 'People', icon: Users, color: 'bg-rose-100 text-rose-700' },
  ];

  const cadences = [
    { value: 'weekly', label: 'Weekly', periods: 52 },
    { value: 'monthly', label: 'Monthly', periods: 12 },
    { value: 'quarterly', label: 'Quarterly', periods: 4 },
    { value: 'yearly', label: 'Yearly', periods: 1 },
  ];

  const requiredColumns = [
    'period_type', 'period_start_date', 'period_end_date',
    'metric_name', 'actual_value'
  ];

  const optionalColumns = ['plan_value', 'unit', 'directionality', 'year', 'segment', 'owner', 'notes'];

  // Fetch available periods when cadence or year changes
  const fetchAvailablePeriods = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/templates/periods/${selectedCadence}?year=${templateYear}`);
      setAvailablePeriods(response.data.periods || []);
      
      // Reset period to 1 if current selection is out of range
      if (selectedPeriod > (response.data.periods?.length || 1)) {
        setSelectedPeriod(1);
      }
    } catch (error) {
      console.error('Failed to fetch periods:', error);
      setAvailablePeriods([]);
    }
  }, [selectedCadence, templateYear, selectedPeriod]);

  // Fetch template info when selection changes
  const fetchTemplateInfo = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API}/templates/${selectedFunction}/info?period_type=${selectedCadence}`
      );
      setTemplateInfo(response.data);
    } catch (error) {
      console.error('Failed to fetch template info:', error);
      setTemplateInfo(null);
    }
  }, [selectedFunction, selectedCadence]);

  const fetchUploads = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/uploads`);
      setUploads(response.data);
    } catch (error) {
      console.error('Failed to fetch uploads:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUploads();
    fetchAvailablePeriods();
    fetchTemplateInfo();
  }, [fetchUploads, fetchAvailablePeriods, fetchTemplateInfo]);

  // Clear deep-link banner after some time
  useEffect(() => {
    if (showDeepLinkBanner) {
      const timer = setTimeout(() => setShowDeepLinkBanner(false), 10000);
      return () => clearTimeout(timer);
    }
  }, [showDeepLinkBanner]);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv'
    ];

    if (!validTypes.includes(selectedFile.type) && 
        !selectedFile.name.endsWith('.xlsx') && 
        !selectedFile.name.endsWith('.csv')) {
      toast.error('Please upload an Excel (.xlsx) or CSV file');
      return;
    }

    setFile(selectedFile);
    setValidationResult(null);
    setShowValidationDetails(false);
    parseFile(selectedFile);
  };

  const parseFile = (selectedFile) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        
        // Collect data from ALL data sheets (not Instructions)
        const dataSheets = workbook.SheetNames.filter(name => 
          name.toLowerCase() !== 'instructions'
        );
        
        let allDataRows = [];
        let allErrors = [];
        let extractedMetadata = null;
        
        dataSheets.forEach(sheetName => {
          const worksheet = workbook.Sheets[sheetName];
          
          // Try to detect template format by checking for header block
          // Look for "Period Type:" or "Start Date:" labels to identify template format
          const cellA4 = worksheet['A4']?.v;
          const cellA6 = worksheet['A6']?.v;
          
          let jsonData;
          
          if (cellA4 === 'Period Type:' || cellA6 === 'Start Date:') {
            // This is a template with header block - data starts at row 11 (index 10)
            // Extract metadata from header block
            const metaStartDate = worksheet['B6']?.v;
            const metaEndDate = worksheet['B7']?.v;
            const metaFunction = worksheet['B8']?.v;
            const metaPeriodType = worksheet['B4']?.v?.toLowerCase();
            
            extractedMetadata = {
              period_start_date: metaStartDate,
              period_end_date: metaEndDate,
              function: metaFunction?.toLowerCase(),
              period_type: metaPeriodType
            };
            
            // Parse with row 10 as header (0-indexed row 9)
            jsonData = XLSX.utils.sheet_to_json(worksheet, { 
              defval: '',
              range: 9  // Start from row 10 (0-indexed)
            });
            
            console.log('Detected template format, extracted metadata:', extractedMetadata);
          } else {
            // Standard format - first row is headers
            jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: '' });
          }
          
          if (jsonData.length === 0) return;
          
          // Check if this sheet has data with actual_value
          jsonData.forEach((row, idx) => {
            const normalizedRow = {};
            Object.entries(row).forEach(([key, value]) => {
              // Normalize column names (handle "Actual Value *" -> "actual_value")
              let normalizedKey = key.toLowerCase().trim()
                .replace(/\s+/g, '_')
                .replace(/\*/g, '')
                .replace(/_+$/, '');  // Remove trailing underscores
              normalizedRow[normalizedKey] = value;
            });
            
            // Skip rows that look like metadata or notes
            const metricName = normalizedRow.metric_name;
            if (!metricName || 
                metricName.toString().startsWith('*') || 
                metricName.toString().includes('Required field')) {
              return;
            }
            
            // Skip rows without actual_value data
            const actualValue = normalizedRow.actual_value;
            if (actualValue === '' || actualValue === undefined || actualValue === null) {
              return;
            }
            
            // Add metadata from template header if available
            if (extractedMetadata) {
              if (!normalizedRow.period_start_date && extractedMetadata.period_start_date) {
                normalizedRow.period_start_date = extractedMetadata.period_start_date;
              }
              if (!normalizedRow.period_end_date && extractedMetadata.period_end_date) {
                normalizedRow.period_end_date = extractedMetadata.period_end_date;
              }
              if (!normalizedRow.period_type && extractedMetadata.period_type) {
                normalizedRow.period_type = extractedMetadata.period_type;
              }
            }
            
            // Validate required columns
            const rowErrors = [];
            requiredColumns.forEach(col => {
              const value = normalizedRow[col];
              if (value === undefined || value === null || value === '') {
                rowErrors.push(`Missing ${col}`);
              }
            });
            
            // Validate actual_value is numeric
            if (actualValue && isNaN(Number(actualValue))) {
              rowErrors.push('actual_value must be a number');
            }
            
            if (rowErrors.length > 0) {
              allErrors.push({ row: `${sheetName}:${idx + 2}`, errors: rowErrors, sheet: sheetName });
            } else {
              allDataRows.push(normalizedRow);
            }
          });
        });

        setValidationErrors(allErrors);
        setParsedData(allDataRows);
        
        if (allDataRows.length === 0) {
          toast.warning('No data rows found with actual_value filled in. Please fill in the actual_value column for rows you want to upload.');
        } else if (allErrors.length === 0) {
          toast.success(`Parsed ${allDataRows.length} valid rows from ${dataSheets.length} sheet(s)`);
        } else {
          toast.warning(`Found ${allErrors.length} validation issues. ${allDataRows.length} rows ready.`);
        }
      } catch (error) {
        console.error('Parse error:', error);
        toast.error('Failed to parse file: ' + error.message);
      }
    };

    reader.readAsArrayBuffer(selectedFile);
  };

  const handleUpload = async () => {
    if (!parsedData || parsedData.length === 0) {
      toast.error('No data to upload');
      return;
    }

    setUploading(true);
    try {
      const response = await axios.post(`${API}/kpi-values/upload`, {
        rows: parsedData,
        function: selectedFunction,
        filename: file?.name || 'upload.xlsx'
      });

      setValidationResult(response.data);
      
      if (response.data.status === 'failed') {
        toast.error(`Upload failed: ${response.data.total_errors} errors found`);
        setShowValidationDetails(true);
      } else if (response.data.total_errors > 0) {
        toast.warning(`Uploaded ${response.data.rows_processed} rows with ${response.data.total_errors} errors`);
        setShowValidationDetails(true);
      } else {
        toast.success(`Successfully uploaded ${response.data.rows_processed} rows (${response.data.inserted} new, ${response.data.updated} updated)`);
      }

      fetchUploads();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleValidateOnly = async () => {
    if (!parsedData || parsedData.length === 0) {
      toast.error('No data to validate');
      return;
    }

    setUploading(true);
    try {
      const response = await axios.post(`${API}/kpi-values/upload`, {
        rows: parsedData,
        function: selectedFunction,
        filename: file?.name || 'upload.xlsx',
        validate_only: true
      });

      setValidationResult(response.data);
      setShowValidationDetails(true);
      
      if (response.data.summary.valid_rows === response.data.summary.total_rows) {
        toast.success(`All ${response.data.summary.total_rows} rows are valid! Ready to upload.`);
      } else {
        toast.info(`Validation complete: ${response.data.summary.valid_rows}/${response.data.summary.total_rows} rows valid`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Validation failed');
    } finally {
      setUploading(false);
    }
  };

  const clearUpload = () => {
    setFile(null);
    setParsedData(null);
    setValidationErrors([]);
    setValidationResult(null);
    setShowValidationDetails(false);
  };

  const downloadTemplate = async (func) => {
    setDownloading(func);
    try {
      const response = await axios.get(
        `${API}/templates/${func}/download?year=${templateYear}&period_type=${selectedCadence}&period_num=${selectedPeriod}`,
        { responseType: 'blob' }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get period label for filename
      const period = availablePeriods.find(p => p.num === selectedPeriod);
      const periodLabel = period ? period.label.replace(/[^a-zA-Z0-9]/g, '_') : selectedPeriod;
      link.setAttribute('download', `pulse_${func}_${selectedCadence}_${periodLabel}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`${func.charAt(0).toUpperCase() + func.slice(1)} ${selectedCadence} template downloaded`);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download template');
    } finally {
      setDownloading(null);
    }
  };

  const downloadCadenceTemplate = async () => {
    setDownloading('cadence');
    try {
      const response = await axios.get(
        `${API}/templates/${selectedFunction}/download?year=${templateYear}&period_type=${selectedCadence}&period_num=${selectedPeriod}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const period = availablePeriods.find(p => p.num === selectedPeriod);
      const periodLabel = period ? period.label.replace(/[^a-zA-Z0-9]/g, '_') : selectedPeriod;
      link.setAttribute('download', `pulse_${selectedFunction}_${selectedCadence}_${periodLabel}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`${selectedFunction.charAt(0).toUpperCase() + selectedFunction.slice(1)} ${selectedCadence} template downloaded`);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download template');
    } finally {
      setDownloading(null);
    }
  };

  const downloadMasterTemplate = async () => {
    setDownloading('master');
    try {
      const response = await axios.get(
        `${API}/templates/download/all?year=${templateYear}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `players_health_pulse_master_template_${templateYear}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Master template downloaded');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download master template');
    } finally {
      setDownloading(null);
    }
  };

  return (
    <Layout>
      <Header title="Upload Center" subtitle="Download templates and upload KPI data" />
      
      <div className="p-4 sm:p-6 space-y-6" data-testid="upload-center">
        {/* Deep-link notification banner */}
        {showDeepLinkBanner && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-blue-900">
                Upload Reminder: {selectedFunction.charAt(0).toUpperCase() + selectedFunction.slice(1)} • {selectedCadence.charAt(0).toUpperCase() + selectedCadence.slice(1)}
              </p>
              <p className="text-sm text-blue-700 mt-1">
                Your template is ready below. Download, fill in your KPI data, and upload.
              </p>
            </div>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setShowDeepLinkBanner(false)}
              className="text-blue-600 hover:text-blue-800 hover:bg-blue-100"
            >
              Dismiss
            </Button>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="templates">Download Templates</TabsTrigger>
            <TabsTrigger value="upload">Upload Data</TabsTrigger>
          </TabsList>

          {/* ============ TEMPLATES TAB ============ */}
          <TabsContent value="templates" className="mt-6 space-y-6">
            {/* Cadence-Specific Template Generator */}
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-6 text-white">
              <h2 className="text-xl font-bold mb-2">Generate Cadence-Specific Template</h2>
              <p className="text-blue-100 mb-6">
                Select your department, cadence, and period to get a simplified template with only the required KPIs
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                {/* Function */}
                <div>
                  <label className="block text-xs font-medium text-blue-200 mb-1.5">Department</label>
                  <Select value={selectedFunction} onValueChange={setSelectedFunction}>
                    <SelectTrigger className="bg-white/10 border-white/20 text-white" data-testid="cadence-function-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {departments.map(d => (
                        <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Year */}
                <div>
                  <label className="block text-xs font-medium text-blue-200 mb-1.5">Year</label>
                  <Select value={String(templateYear)} onValueChange={(v) => setTemplateYear(Number(v))}>
                    <SelectTrigger className="bg-white/10 border-white/20 text-white" data-testid="cadence-year-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="2024">2024</SelectItem>
                      <SelectItem value="2025">2025</SelectItem>
                      <SelectItem value="2026">2026</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Cadence */}
                <div>
                  <label className="block text-xs font-medium text-blue-200 mb-1.5">Cadence</label>
                  <Select value={selectedCadence} onValueChange={setSelectedCadence}>
                    <SelectTrigger className="bg-white/10 border-white/20 text-white" data-testid="cadence-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {cadences.map(c => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Period */}
                <div>
                  <label className="block text-xs font-medium text-blue-200 mb-1.5">Period</label>
                  <Select value={String(selectedPeriod)} onValueChange={(v) => setSelectedPeriod(Number(v))}>
                    <SelectTrigger className="bg-white/10 border-white/20 text-white" data-testid="cadence-period-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {availablePeriods.map(p => (
                        <SelectItem key={p.num} value={String(p.num)}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Template Info */}
              {templateInfo && (
                <div className="bg-white/10 rounded-lg p-4 mb-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-blue-100">
                        Template will include <span className="font-semibold text-white">{templateInfo.total_kpis} KPIs</span> required for {selectedCadence} uploads
                      </p>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {Object.keys(templateInfo.sections || {}).map(section => (
                          <span key={section} className="text-xs bg-white/20 px-2 py-1 rounded">
                            {section}: {templateInfo.sections[section].length} KPIs
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              <Button 
                onClick={downloadCadenceTemplate}
                disabled={downloading === 'cadence' || !availablePeriods.length}
                className="bg-white text-blue-700 hover:bg-blue-50"
                data-testid="download-cadence-template"
              >
                {downloading === 'cadence' ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <FileDown className="w-4 h-4 mr-2" />
                )}
                Download {selectedCadence.charAt(0).toUpperCase() + selectedCadence.slice(1)} Template
              </Button>
            </div>

            {/* Year Selection for Legacy Templates */}
            <div className="bg-white rounded-lg border border-slate-200 p-4">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">All-Period Templates</h2>
                  <p className="text-sm text-slate-500">Download templates with all period types (Weekly, Monthly, Quarterly, Yearly)</p>
                </div>
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-slate-400" />
                  <Select value={String(templateYear)} onValueChange={(v) => setTemplateYear(Number(v))}>
                    <SelectTrigger className="w-[100px]" data-testid="template-year-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="2023">2023</SelectItem>
                      <SelectItem value="2024">2024</SelectItem>
                      <SelectItem value="2025">2025</SelectItem>
                      <SelectItem value="2026">2026</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Master Template */}
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-6 text-white">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h3 className="text-xl font-bold">Master Template</h3>
                  <p className="text-blue-100 mt-1">
                    All 5 departments in one file with Monthly data sheets
                  </p>
                  <p className="text-sm text-blue-200 mt-2">
                    Includes: Brokerage, MGA, Marketing, Finance, People
                  </p>
                </div>
                <Button 
                  onClick={downloadMasterTemplate}
                  disabled={downloading === 'master'}
                  className="bg-white text-blue-600 hover:bg-blue-50 shrink-0"
                  data-testid="download-master-template"
                >
                  {downloading === 'master' ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <FileDown className="w-4 h-4 mr-2" />
                  )}
                  Download Master
                </Button>
              </div>
            </div>

            {/* Department Templates */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {departments.map((dept) => {
                const Icon = dept.icon;
                return (
                  <div 
                    key={dept.value}
                    className="bg-white rounded-lg border border-slate-200 p-5 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between">
                      <div className={cn("p-2 rounded-lg", dept.color)}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <span className="text-xs text-slate-400">{templateYear}</span>
                    </div>
                    <h3 className="font-semibold text-slate-900 mt-3">{dept.label}</h3>
                    <p className="text-sm text-slate-500 mt-1">
                      Weekly, Monthly, Quarterly, Yearly sheets
                    </p>
                    <Button 
                      variant="outline" 
                      className="w-full mt-4"
                      onClick={() => downloadTemplate(dept.value)}
                      disabled={downloading === dept.value}
                      data-testid={`download-${dept.value}-template`}
                    >
                      {downloading === dept.value ? (
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4 mr-2" />
                      )}
                      Download Template
                    </Button>
                  </div>
                );
              })}
            </div>

            {/* Template Info */}
            <div className="bg-slate-50 rounded-lg border border-slate-200 p-5">
              <h3 className="font-semibold text-slate-900 mb-3">Template Features</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-slate-600">
                <div>
                  <p className="font-medium text-slate-700 mb-2">Each template includes:</p>
                  <ul className="space-y-1 list-disc list-inside">
                    <li>Instructions sheet with detailed guidance</li>
                    <li>Pre-filled metric names and units</li>
                    <li>Correct date ranges for each period</li>
                    <li>Dropdown validation for segment column</li>
                  </ul>
                </div>
                <div>
                  <p className="font-medium text-slate-700 mb-2">Period types available:</p>
                  <ul className="space-y-1 list-disc list-inside">
                    <li><strong>Weekly</strong>: 52 weeks (Week 1-52)</li>
                    <li><strong>Monthly</strong>: 12 months (Jan-Dec)</li>
                    <li><strong>Quarterly</strong>: 4 quarters (Q1-Q4)</li>
                    <li><strong>Yearly</strong>: Full year aggregate</li>
                  </ul>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* ============ UPLOAD TAB ============ */}
          <TabsContent value="upload" className="mt-6 space-y-6">
            <div className="bg-white rounded-lg border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">
                Upload KPI Data
              </h2>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left - Function Selection */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Select Department
                    </label>
                    <Select value={selectedFunction} onValueChange={setSelectedFunction}>
                      <SelectTrigger data-testid="function-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {departments.map(d => (
                          <SelectItem key={d.value} value={d.value}>
                            {d.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="p-4 bg-slate-50 rounded-lg text-sm text-slate-600">
                    <p className="font-medium mb-2">Required Columns:</p>
                    <div className="flex flex-wrap gap-2">
                      {requiredColumns.map(col => (
                        <span key={col} className="bg-white px-2 py-1 rounded text-xs border">
                          {col}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Right - File Upload */}
                <div className="space-y-4">
                  <div
                    className={cn(
                      "border-2 border-dashed rounded-lg p-8 text-center transition-colors",
                      file ? "border-blue-300 bg-blue-50" : "border-slate-300 hover:border-slate-400"
                    )}
                  >
                    <input
                      type="file"
                      accept=".xlsx,.csv"
                      onChange={handleFileSelect}
                      className="hidden"
                      id="file-upload"
                      data-testid="file-input"
                    />
                    <label htmlFor="file-upload" className="cursor-pointer">
                      {file ? (
                        <>
                          <FileSpreadsheet className="w-12 h-12 text-blue-500 mx-auto mb-3" />
                          <p className="text-sm font-medium text-slate-700">{file.name}</p>
                          <p className="text-xs text-slate-500 mt-1">
                            {parsedData ? `${parsedData.length} rows with data` : 'Parsing...'}
                          </p>
                        </>
                      ) : (
                        <>
                          <Upload className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                          <p className="text-sm font-medium text-slate-700">
                            Drop your file here or click to browse
                          </p>
                          <p className="text-xs text-slate-500 mt-1">
                            Supports .xlsx and .csv files
                          </p>
                        </>
                      )}
                    </label>
                  </div>

                  {file && (
                    <div className="flex gap-2">
                      <Button
                        onClick={handleValidateOnly}
                        disabled={uploading || validationErrors.some(e => e.row === 'Header') || !parsedData?.length}
                        variant="outline"
                        className="flex-1"
                        data-testid="validate-btn"
                      >
                        {uploading ? (
                          <>
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            Validating...
                          </>
                        ) : (
                          <>
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Validate First
                          </>
                        )}
                      </Button>
                      <Button
                        onClick={handleUpload}
                        disabled={uploading || validationErrors.some(e => e.row === 'Header') || !parsedData?.length}
                        className="flex-1 bg-[#2563EB] hover:bg-[#1D4ED8]"
                        data-testid="upload-btn"
                      >
                        {uploading ? (
                          <>
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4 mr-2" />
                            Upload {parsedData?.length || 0} Rows
                          </>
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={clearUpload}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* Enhanced Validation Results Panel */}
              {validationResult && showValidationDetails && (
                <div className="mt-6 space-y-4">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-slate-50 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-slate-900">
                        {validationResult.summary?.total_rows || validationResult.rows_processed + validationResult.total_errors || 0}
                      </p>
                      <p className="text-sm text-slate-500">Total Rows</p>
                    </div>
                    <div className="bg-emerald-50 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-emerald-700">
                        {validationResult.summary?.valid_rows || validationResult.rows_processed || 0}
                      </p>
                      <p className="text-sm text-emerald-600">Valid</p>
                    </div>
                    <div className="bg-rose-50 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-rose-700">
                        {validationResult.summary?.error_rows || validationResult.total_errors || 0}
                      </p>
                      <p className="text-sm text-rose-600">Errors</p>
                    </div>
                    <div className="bg-amber-50 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-amber-700">
                        {validationResult.summary?.warning_rows || validationResult.total_warnings || 0}
                      </p>
                      <p className="text-sm text-amber-600">Warnings</p>
                    </div>
                  </div>

                  {/* Statistics */}
                  {validationResult.statistics && (
                    <div className="bg-slate-50 rounded-lg p-4">
                      <h4 className="font-medium text-slate-900 mb-3">Upload Statistics</h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                        {validationResult.years_detected?.length > 0 && (
                          <div>
                            <span className="text-slate-500">Years detected:</span>
                            <span className="ml-2 font-medium">{validationResult.years_detected.join(', ')}</span>
                          </div>
                        )}
                        {validationResult.statistics.duplicates_in_file > 0 && (
                          <div className="text-amber-600">
                            <span>Duplicates in file:</span>
                            <span className="ml-2 font-medium">{validationResult.statistics.duplicates_in_file}</span>
                          </div>
                        )}
                        {validationResult.statistics.date_issues > 0 && (
                          <div className="text-rose-600">
                            <span>Date format issues:</span>
                            <span className="ml-2 font-medium">{validationResult.statistics.date_issues}</span>
                          </div>
                        )}
                        {validationResult.inserted > 0 && (
                          <div className="text-emerald-600">
                            <span>New records:</span>
                            <span className="ml-2 font-medium">{validationResult.inserted}</span>
                          </div>
                        )}
                        {validationResult.updated > 0 && (
                          <div className="text-blue-600">
                            <span>Updated records:</span>
                            <span className="ml-2 font-medium">{validationResult.updated}</span>
                          </div>
                        )}
                        {Object.keys(validationResult.statistics?.by_period_type || {}).length > 0 && (
                          <div>
                            <span className="text-slate-500">By period:</span>
                            <span className="ml-2 font-medium">
                              {Object.entries(validationResult.statistics.by_period_type)
                                .map(([k, v]) => `${k}: ${v}`)
                                .join(', ')}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Unknown Metrics Warning */}
                  {validationResult.unknown_metrics?.length > 0 && (
                    <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                        <div>
                          <h4 className="font-medium text-amber-800">Unknown Metric Names</h4>
                          <p className="text-sm text-amber-700 mt-1">
                            The following metrics are not defined in KPI Definitions for this department:
                          </p>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {validationResult.unknown_metrics.map((m, i) => (
                              <span key={i} className="bg-amber-100 text-amber-800 px-2 py-1 rounded text-xs font-medium">
                                {m}
                              </span>
                            ))}
                          </div>
                          <p className="text-xs text-amber-600 mt-2">
                            Tip: Add these metrics in KPI Definitions page or verify spelling
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Detailed Errors */}
                  {validationResult.errors?.length > 0 && (
                    <div className="p-4 bg-rose-50 rounded-lg border border-rose-200">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <AlertTriangle className="w-5 h-5 text-rose-600" />
                          <h4 className="font-medium text-rose-800">
                            Validation Errors ({validationResult.total_errors || validationResult.errors.length})
                          </h4>
                        </div>
                      </div>
                      <div className="space-y-3 max-h-60 overflow-y-auto">
                        {validationResult.errors.slice(0, 20).map((error, idx) => (
                          <div key={idx} className="bg-white rounded p-3 border border-rose-100">
                            <div className="flex items-start justify-between">
                              <span className="font-medium text-rose-700">Row {error.row}</span>
                              <span className="text-xs bg-rose-100 text-rose-700 px-2 py-0.5 rounded">
                                {error.type || 'error'}
                              </span>
                            </div>
                            <ul className="mt-2 space-y-1">
                              {error.errors?.map((e, i) => (
                                <li key={i} className="text-sm text-rose-600 flex items-start gap-1">
                                  <span className="text-rose-400">•</span>
                                  <span>{e}</span>
                                </li>
                              ))}
                            </ul>
                            {error.suggestion && (
                              <p className="text-xs text-slate-500 mt-2 italic">💡 {error.suggestion}</p>
                            )}
                          </div>
                        ))}
                        {validationResult.errors.length > 20 && (
                          <p className="text-sm text-rose-600 text-center py-2">
                            ...and {validationResult.errors.length - 20} more errors
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Warnings */}
                  {validationResult.warnings?.length > 0 && (
                    <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                      <div className="flex items-center gap-2 mb-3">
                        <AlertTriangle className="w-5 h-5 text-amber-600" />
                        <h4 className="font-medium text-amber-800">
                          Warnings ({validationResult.total_warnings || validationResult.warnings.length})
                        </h4>
                      </div>
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {validationResult.warnings.slice(0, 10).map((warning, idx) => (
                          <div key={idx} className="text-sm bg-white rounded p-2 border border-amber-100">
                            <span className="font-medium text-amber-700">Row {warning.row}:</span>
                            <ul className="mt-1 pl-4">
                              {warning.warnings?.map((w, i) => (
                                <li key={i} className="text-amber-600">{w}</li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Success message for validation preview */}
                  {validationResult.mode === 'validation_preview' && validationResult.summary?.can_upload && (
                    <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-emerald-600" />
                        <div>
                          <h4 className="font-medium text-emerald-800">Ready to Upload</h4>
                          <p className="text-sm text-emerald-600">
                            {validationResult.summary.valid_rows} rows passed validation. Click &quot;Upload&quot; to save the data.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Legacy Validation Errors (client-side parsing) */}
              {validationErrors.length > 0 && !validationResult && (
                <div className="mt-6 p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <div className="flex items-center gap-2 mb-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                    <h3 className="font-medium text-amber-800">Validation Issues</h3>
                  </div>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {validationErrors.slice(0, 10).map((error, idx) => (
                      <div key={idx} className="text-sm">
                        <span className="font-medium text-amber-700">Row {error.row}:</span>
                        <span className="text-amber-600 ml-2">{error.errors.join(', ')}</span>
                      </div>
                    ))}
                    {validationErrors.length > 10 && (
                      <p className="text-sm text-amber-600">
                        ...and {validationErrors.length - 10} more issues
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Upload History */}
            <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-200">
                <h2 className="text-lg font-semibold text-slate-900">Upload History</h2>
              </div>
              
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="w-[200px]">Filename</TableHead>
                    <TableHead>Function</TableHead>
                    <TableHead>Rows</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Uploaded By</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {uploads.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-slate-400">
                        No uploads yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    uploads.map((upload) => (
                      <TableRow key={upload.id}>
                        <TableCell className="font-medium">{upload.filename}</TableCell>
                        <TableCell className="capitalize">{upload.function}</TableCell>
                        <TableCell>{upload.row_count}</TableCell>
                        <TableCell>
                          <span className={cn(
                            "inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium",
                            upload.status === 'success' 
                              ? 'bg-emerald-100 text-emerald-700'
                              : 'bg-amber-100 text-amber-700'
                          )}>
                            {upload.status === 'success' 
                              ? <CheckCircle className="w-3 h-3" />
                              : <AlertTriangle className="w-3 h-3" />
                            }
                            {upload.status}
                          </span>
                        </TableCell>
                        <TableCell>{upload.uploaded_by_name}</TableCell>
                        <TableCell>
                          {new Date(upload.created_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default UploadCenter;
